import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import { useSessionTypes } from "../hooks/useSessionTypes";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "../components/ui/sonner";

const EMPTY = {
  title: "",
  description: "",
  session_type: "friendship",
  city: "",
  state: "",
  zip_code: "",
  address: "",
  session_date: "",
  session_time: "",
  duration_minutes: 90,
  price: 45,
  capacity: 6,
  image_url: "",
  what_to_expect: "",
  what_to_wear: "",
  whats_included: "",
  safety_info: "",
  cancellation_policy: "",
  payment_timing: "",
  pay_in_person_enabled: true,
  venmo_enabled: false,
  venmo_username: "",
  cashapp_enabled: false,
  cashapp_username: "",
  other_payment_method: "",
  custom_payment_instructions: "",
};

export default function SessionForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const sessionTypes = useSessionTypes();
  const isEdit = Boolean(id);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isEdit) {
      api.get(`/sessions/${id}`).then(({ data }) => {
        setForm({
          ...data,
          whats_included: (data.whats_included || []).join(", "),
          payment_timing: data.payment_timing || "",
          venmo_username: data.venmo_username || "",
          cashapp_username: data.cashapp_username || "",
          custom_payment_instructions: data.custom_payment_instructions || "",
        });
        setLoading(false);
      });
    } else {
      setForm((f) => ({
        ...f,
        capacity: settings.default_session_capacity ?? f.capacity,
        cancellation_policy: settings.default_cancellation_policy || f.cancellation_policy,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit, settings.default_session_capacity, settings.default_cancellation_policy]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      duration_minutes: Number(form.duration_minutes),
      price: Number(form.price),
      capacity: Number(form.capacity),
      whats_included: form.whats_included
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    };
    try {
      if (isEdit) {
        await api.put(`/sessions/${id}`, payload);
        toast.success("Session updated");
      } else {
        await api.post("/sessions", payload);
        toast.success("Session created");
      }
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="py-32 text-center font-medium">Loading session...</div>;

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-14" data-testid="session-form-page">
      <h1 className="font-extrabold uppercase text-2xl sm:text-3xl tracking-tight mb-8">{isEdit ? "Edit Session" : "Create a Stranger Session"}</h1>
      <form onSubmit={handleSubmit} className="space-y-5" data-testid="session-form">
        <Input data-testid="session-title-input" placeholder="Session title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <Textarea data-testid="session-description-input" placeholder="Description" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <Select value={form.session_type} onValueChange={(v) => setForm({ ...form, session_type: v })}>
          <SelectTrigger data-testid="session-type-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {sessionTypes.map((t) => (
              <SelectItem key={t.type_key} value={t.type_key}>
                {t.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="grid grid-cols-3 gap-3">
          <Input data-testid="session-city-input" placeholder="City" required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
          <Input data-testid="session-state-input" placeholder="State" required value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} />
          <Input data-testid="session-zip-input" placeholder="ZIP code" required value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} />
        </div>
        <Input data-testid="session-address-input" placeholder="Location / address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
        <div className="grid grid-cols-2 gap-3">
          <Input data-testid="session-date-input" type="date" required value={form.session_date} onChange={(e) => setForm({ ...form, session_date: e.target.value })} />
          <Input data-testid="session-time-input" type="time" required value={form.session_time} onChange={(e) => setForm({ ...form, session_time: e.target.value })} />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Input data-testid="session-duration-input" type="number" placeholder="Duration (min)" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} />
          <Input data-testid="session-price-input" type="number" placeholder="Price per Stranger" required value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
          <Input data-testid="session-capacity-input" type="number" placeholder="Capacity" required value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} />
        </div>
        <Input data-testid="session-image-input" placeholder="Image URL" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} />
        <Textarea data-testid="session-what-to-expect-input" placeholder="What to expect" value={form.what_to_expect} onChange={(e) => setForm({ ...form, what_to_expect: e.target.value })} />
        <Textarea data-testid="session-what-to-wear-input" placeholder="What to wear" value={form.what_to_wear} onChange={(e) => setForm({ ...form, what_to_wear: e.target.value })} />
        <Input data-testid="session-included-input" placeholder="What's included (comma separated)" value={form.whats_included} onChange={(e) => setForm({ ...form, whats_included: e.target.value })} />
        <Textarea data-testid="session-safety-input" placeholder="Safety information" value={form.safety_info} onChange={(e) => setForm({ ...form, safety_info: e.target.value })} />
        <Textarea data-testid="session-cancellation-input" placeholder="Cancellation policy" value={form.cancellation_policy} onChange={(e) => setForm({ ...form, cancellation_policy: e.target.value })} />

        <div className="border border-soft-gray rounded-2xl p-5">
          <h2 className="font-extrabold uppercase text-sm mb-1">Your Session Fee Payment (Optional Override)</h2>
          <p className="text-xs font-medium text-midnight-black/50 mb-4">Leave blank to use your default Payment Settings from the dashboard. This is how Strangers pay YOU — separate from the platform booking fee.</p>
          <Select value={form.payment_timing || "default"} onValueChange={(v) => setForm({ ...form, payment_timing: v === "default" ? "" : v })}>
            <SelectTrigger data-testid="session-payment-timing-select" className="mb-3"><SelectValue placeholder="Use my default timing" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="default">Use my default timing</SelectItem>
              <SelectItem value="pay_in_person">Pay In Person</SelectItem>
              <SelectItem value="pay_before_session">Pay Before Session</SelectItem>
              <SelectItem value="pay_after_session">Pay After Session</SelectItem>
              <SelectItem value="contact_photographer">Contact Me to Arrange</SelectItem>
              <SelectItem value="custom">Custom</SelectItem>
            </SelectContent>
          </Select>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <label className="flex items-center gap-2 text-sm font-medium">
              <input type="checkbox" data-testid="session-venmo-enabled" checked={form.venmo_enabled} onChange={(e) => setForm({ ...form, venmo_enabled: e.target.checked })} /> Accept Venmo
            </label>
            <Input data-testid="session-venmo-username-input" placeholder="Venmo username" disabled={!form.venmo_enabled} value={form.venmo_username || ""} onChange={(e) => setForm({ ...form, venmo_username: e.target.value })} />
            <label className="flex items-center gap-2 text-sm font-medium">
              <input type="checkbox" data-testid="session-cashapp-enabled" checked={form.cashapp_enabled} onChange={(e) => setForm({ ...form, cashapp_enabled: e.target.checked })} /> Accept Cash App
            </label>
            <Input data-testid="session-cashapp-username-input" placeholder="Cash App username" disabled={!form.cashapp_enabled} value={form.cashapp_username || ""} onChange={(e) => setForm({ ...form, cashapp_username: e.target.value })} />
          </div>
          <Textarea data-testid="session-custom-payment-instructions-input" placeholder="Custom payment instructions (optional)" value={form.custom_payment_instructions || ""} onChange={(e) => setForm({ ...form, custom_payment_instructions: e.target.value })} />
        </div>

        <Button type="submit" data-testid="save-session-button" disabled={saving} className="bg-stranger-orange hover:bg-orange-600 text-clean-white font-bold rounded-full px-8 py-6">
          {saving ? "Saving..." : isEdit ? "SAVE CHANGES" : "CREATE SESSION"}
        </Button>
      </form>
    </div>
  );
}
